import { DEFAULT_TEMPLATE_ACCENT_COLOR, normalizeTemplateAccentColor } from "@exibra/shared";
import { Label } from "@/components/ui/label";
import { Palette } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

/**
 * Props for the template-level LED accent color picker.
 */
interface AccentColorFieldProps {
    /** Current template LED accent color. */
    value?: string | null;
    /** Called when the user selects a new LED accent color. */
    onChange: (value: string) => void;
    /** Optional class names for embedding the field in different editor panels. */
    className?: string;
}

/**
 * Lets editors pick the RGB accent color that capable presenters mirror to paired LED strips.
 */
export function AccentColorField({ value, onChange, className }: AccentColorFieldProps) {
    const { t } = useTranslation();
    const color = normalizeTemplateAccentColor(value) ?? DEFAULT_TEMPLATE_ACCENT_COLOR;

    return (
        <div className={cn("flex items-center gap-2", className)}>
            <Label
                htmlFor="template-accent-color"
                className="text-xs whitespace-nowrap flex items-center gap-1.5"
            >
                <Palette className="h-3.5 w-3.5" />
                {t("templates.editor.ledAccent")}
            </Label>
            <input
                id="template-accent-color"
                type="color"
                value={color}
                onChange={(event) => onChange(event.target.value)}
                className="h-9 w-12 cursor-pointer rounded border border-input bg-background p-1"
                aria-label={t("templates.editor.ledAccentColor")}
            />
            <span className="text-xs font-mono text-muted-foreground tabular-nums">{color}</span>
        </div>
    );
}
